import "./App.css";
import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Home from "./Home";
import Cards from "./Cards";
import Profile from "./Profile";
import Cookies from "universal-cookie";
import raw from "./bannedWords.txt";
import ContactForm from "./ContactForm";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RegisterSuccess from "./users/RegisterSuccess";
import CookieConsent from "react-cookie-consent";
import Articles from "./articles/Articles";
import Login from "./users/Login";
import Register from "./users/Register";
import UserAccount from "./users/UserAccount";
import Article from "./articles/Article";
import Events from "./Events";
import EventHistory from "./EventHistory";
import Search from "./Search";
import AdminPanel from "./users/AdminPanel";
import About from "./About";
import PasswordReset from "./users/PasswordReset";
import PrivacyPolicy from "./PrivacyPolicy";
import UsingCookies from "./UsingCookies";
import CategoryInformation from "./CategoryInformation";
import CategoryLanding from "./CategoryLanding";
import { LanguageProvider } from "./LanguageContext";
import { checkUserTargetId } from "./placeUtils";
import useScrollRestoration from "../hooks/useScrollRestoration";

function ScrollManager() {
  useScrollRestoration();
  return null;
}

const SESSION_HOLD_TIME = 10800;
const EMPTY_USER = {
  username: "",
  email: "",
  active: false,
  created: {},
  banned: false,
  _id: "",
  adminId: "",
};

const PLACE_SECTIONS = [
  {
    path: "kultura",
    landing: "kultura",
    children: [
      { path: "muzea", category: "Muzea" },
      { path: "teatry", category: "Teatry" },
      { path: "kina", category: "Kina" },
      { path: "zabytki", category: "Zabytki" },
      { path: "kulturaInne", category: "KulturaInne" },
    ],
  },
  {
    path: "gastronomia",
    landing: "gastronomia",
    children: [
      { path: "restauracje", category: "Restauracje" },
      { path: "fastFoody", category: "Fast Foody" },
      { path: "kawiarnie", category: "Kawiarnie" },
      { path: "desery", category: "Desery" },
      { path: "gastronomiaInne", category: "GastronomiaInne" },
    ],
  },
  {
    path: "nauka",
    landing: "nauka",
    children: [
      { path: "biblioteki", category: "Biblioteki" },
      { path: "ksiegarnie", category: "Księgarnie" },
      { path: "kursy", category: "Kursy" },
      { path: "targi", category: "Targi i kiermasze" },
      { path: "naukaInne", category: "NaukaInne" },
    ],
  },
];

function getPreferenceCookie(name, fallback) {
  try {
    const cookie = document.cookie.split("; ").find((entry) => entry.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : fallback;
  } catch (error) {
    return fallback;
  }
}

function setPreferenceCookie(name, value) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=31536000; path=/; samesite=lax`;
  } catch (error) {
  }
}

function App() {
  const [theme, setTheme] = useState(() => (getPreferenceCookie("wolne-theme", "light") === "dark" ? "dark" : "light"));
  const [language, setLanguage] = useState(() => (getPreferenceCookie("wolne-language", "pl") === "en" ? "en" : "pl"));
  const [userLogged, setUserLogged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addressIp] = useState("");
  const [bannedWords, setBannedWords] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [userData, setUserData] = useState(EMPTY_USER);
  const cookieCheckedRef = useRef(false);

  useEffect(() => {
    checkForCookie();
  }, []);

  useEffect(() => {
    fetch(raw)
      .then((response) => response.text())
      .then((text) => setBannedWords(text.split(/\r?\n/).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPreferenceCookie("wolne-theme", theme);
    document.body.style.backgroundColor = theme === "dark" ? "#17211f" : "#ffffff";
  }, [theme]);

  useEffect(() => {
    setPreferenceCookie("wolne-language", language);
    document.documentElement.lang = language;
  }, [language]);

  function destroyCookie() {
    new Cookies().remove("account");
  }

  function createCookie(data) {
    if (!data) return;
    const cookies = new Cookies();
    data.sessionStarted = new Date();
    cookies.set("account", data, { path: "/" });
  }

  function checkForCookie() {
    if (cookieCheckedRef.current) return;
    cookieCheckedRef.current = true;

    const cookie = new Cookies().get("account");
    if (!cookie?.username || !cookie.email || !cookie._id || cookie.banned === true) {
      if (cookie) destroyCookie();
      return;
    }

    if (Math.round((new Date() - new Date(cookie.sessionStarted)) / 1000) > SESSION_HOLD_TIME) {
      destroyCookie();
      return;
    }

    setUserData({
      username: cookie.username,
      email: cookie.email,
      active: cookie.active,
      created: cookie.created,
      banned: cookie.banned === true,
      _id: cookie._id,
      adminId: cookie.adminId,
    });
    setUserLogged(true);
  }

  function userLoggedOut() {
    setUserLogged(false);
    setUserData(EMPTY_USER);
    destroyCookie();
    toast.success("wylogowano pomyślnie");
  }

  function changeState(category) {
    switch (category) {
      case "Start":
      case "Articles":
      case "Muzea":
      case "Teatry":
      case "Kina":
      case "Zabytki":
      case "KulturaInne":
      case "Biblioteki":
      case "Kursy":
      case "Księgarnie":
      case "TargiIKiermasze":
      case "NaukaInne":
      case "Restauracje":
      case "FastFoody":
      case "Fast Foody":
      case "fastFoody":
      case "Kawiarnie":
      case "Desery":
      case "GastronomiaInne":
        setPageNumber(0);
        break;
      case "UserLoggedIn":
      case "zalogowano pomyślnie":
        setUserLogged(true);
        toast.success("zalogowano pomyślnie");
        break;
      case "UserLoggedOut":
      case "wylogowano pomyślnie":
        userLoggedOut();
        break;
      default:
        break;
    }
  }

  const eventsElement = (
    <Events
      category="Eventy"
      loading={loading}
      setLoading={setLoading}
      pageNumber={pageNumber}
      setPageNumber={setPageNumber}
    />
  );

  const cardsProps = {
    loading,
    setLoading,
    pageNumber,
    setPageNumber,
  };

  const isLoggedIn = userData.username !== "";

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <LanguageProvider language={language}>
        <div className={`app-shell theme-${theme}`}>
          <Navbar
            handleClick={changeState}
            userLogged={userLogged}
            userData={userData}
            theme={theme}
            language={language}
            onThemeToggle={() => setTheme((current) => (current === "light" ? "dark" : "light"))}
            onLanguageToggle={() => setLanguage((current) => (current === "pl" ? "en" : "pl"))}
          />
          <div className="appBody">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="artykuly" element={<Articles />} />
              <Route path="artykuly/:id" element={<Article />} />
              <Route path="kontakt" element={<ContactForm userLogged={userLogged} userData={userData} />} />
              <Route path="onas" element={<About userLogged={userLogged} userData={userData} />} />
              <Route path="polityka-prywatnosci" element={<PrivacyPolicy userLogged={userLogged} userData={userData} />} />
              <Route path="cookies" element={<UsingCookies userLogged={userLogged} userData={userData} />} />
              <Route
                path=":category/:subcategory/profil/:id"
                element={<Profile target="place" userLogged={userLogged} loading={loading} userData={userData} bannedWords={bannedWords} addressIp={addressIp} />}
              />
              <Route
                path=":category/:subcategory/profil_event/:id"
                element={<Profile target="event" userLogged={userLogged} loading={loading} userData={userData} bannedWords={bannedWords} addressIp={addressIp} />}
              />
              <Route
                path="szukaj"
                element={
                  <Search addressIp={addressIp} />
                }
              />
              <Route path="eventy" element={eventsElement} />
              <Route path="eventy/historia" element={<EventHistory />} />
              <Route
                path="logowanie"
                element={isLoggedIn ? <Navigate to="/" /> : <Login setUserData={setUserData} setUserState={changeState} createCookie={createCookie} addressIp={addressIp} />}
              />
              <Route
                path="uzytkownik"
                element={isLoggedIn ? <UserAccount userData={userData} setState={changeState} addressIp={addressIp} /> : <Navigate to="/" />}
              />
              <Route
                path="adminPanel"
                element={isLoggedIn && checkUserTargetId() ? <AdminPanel userData={userData} setState={changeState} addressIp={addressIp} /> : <Navigate to="/" />}
              />
              <Route
                path="rejestracja"
                element={isLoggedIn ? <Navigate to="/" /> : <Register setUserData={setUserData} setUserState={changeState} createCookie={createCookie} bannedWords={bannedWords} addressIp={addressIp} />}
              />
              <Route path="rejestracja/:status" element={<RegisterSuccess />} />
              <Route path="resetHasla" element={<PasswordReset userData={userData} setState={changeState} addressIp={addressIp} />} />
              <Route path="resetHasla/:veriString" element={<PasswordReset userData={userData} setState={changeState} addressIp={addressIp} />} />
              <Route path="informacje/:category" element={<CategoryInformation userLogged={userLogged} userData={userData} />} />

              {PLACE_SECTIONS.map((section) => (
                <Route path={section.path} key={section.path}>
                  <Route index element={<CategoryLanding category={section.landing} />} />
                  {section.children.map((child) => (
                    <Route
                      path={child.path}
                      key={child.path}
                      element={<Cards {...cardsProps} category={child.category} />}
                    />
                  ))}
                </Route>
              ))}

              <Route path="imprezy">
                <Route path="koncerty" element={eventsElement} />
              </Route>
            </Routes>
          </div>
          <ScrollManager />
          <Footer
            handleClick={changeState}
            userData={userData}
            theme={theme}
            onThemeToggle={() => setTheme((current) => (current === "light" ? "dark" : "light"))}
          />
          <ToastContainer
            position="top-left"
            autoClose={1100}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={theme}
          />
          <CookieConsent
            location="bottom"
            buttonText="Zgadzam się"
            cookieName="cookieInfo"
            style={{ background: "#2B373B" }}
            buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
            expires={150}
          >
            Ta strona wykorzystuje pliki cookies w celu prawidłowego działania jej wszystkich funkcji.
          </CookieConsent>
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
